import { SearchX } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { SkuCard } from './sku-card';
import type { Monitor } from '@/lib/api';

export interface SkuGridProps {
  monitors: Monitor[];
  loading: boolean;
  error?: string | null;
  onSelect?: (m: Monitor) => void;
  onClearFilters?: () => void;
}

export function SkuGrid({ monitors, loading, error, onSelect, onClearFilters }: SkuGridProps) {
  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="overflow-hidden rounded-xl border bg-card">
            <Skeleton className="aspect-video w-full rounded-none" />
            <div className="space-y-2 p-3">
              <Skeleton className="h-3 w-1/3" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <div className="flex gap-1 pt-1">
                <Skeleton className="h-5 w-10" />
                <Skeleton className="h-5 w-12" />
                <Skeleton className="h-5 w-14" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed bg-card py-16 text-center">
        <div className="rounded-full bg-destructive/10 p-3 text-destructive">
          <SearchX className="h-6 w-6" />
        </div>
        <div>
          <h3 className="text-base font-semibold">Lỗi tải dữ liệu</h3>
          <p className="mt-1 text-sm text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  if (!monitors.length) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed bg-card py-16 text-center">
        <div className="rounded-full bg-muted p-3 text-muted-foreground">
          <SearchX className="h-6 w-6" />
        </div>
        <div>
          <h3 className="text-base font-semibold">Không tìm thấy SKU nào</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Thử bỏ bớt filter hoặc đổi từ khóa tìm kiếm.
          </p>
        </div>
        {onClearFilters && (
          <Button variant="outline" size="sm" onClick={onClearFilters}>
            Reset filter
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {monitors.map((m) => (
        <SkuCard key={m.id} monitor={m} onSelect={onSelect} />
      ))}
    </div>
  );
}
